import { apiClient } from '../utils/apiClient';

export interface IntroducedInvestorDetailsDto {
  uniqueCode?: string;
  dataverseInvestorId: string;
  email: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  mobile: string;
  brokerName: string;
  serviceProviderType: string;
  productName: string;
  planName: string;
  schemeName: string;
  nationalityName: string;
  countryOfResidenceName?: string;
  investorTypeName: string;
  emailAlreadyExists: boolean;
  /** Set only when this person clicked Discontinue earlier and has not registered since. */
  discontinued?: boolean;
  discontinuedOn?: string;
  isdCode?: string;      // Dataverse country-of-residence ISD GUID
  countryCode?: string;  // resolved dialing code, e.g. "+65"
  // Step 4 prefill: the introduction's nationality/country GUIDs (sent back unchanged) + a
  // server-resolved India flag that toggles the India (PAN) vs non-India (OCI) section.
  nationalityGuid?: string;
  countryOfResidenceGuid?: string;
  nationalityIsIndia?: boolean;
  /** "Yes" / "No" captured at Step 1; read back by Step 4 so its payload is unchanged. */
  ociCardStatus?: string;
  panCardStatus?: string;
  indianOrigin?: string;
}

export interface Step1RequestDto {
  uniqueCode: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dateOfBirth: string; // Format: yyyy-MM-dd
  gender: string;
  email: string;
  mobileNumber: string;
  countryCode: string;
  /** Reference from the introduction; shown read-only and posted back unchanged. */
  countryOfResidence?: string;
  /** "Yes" / "No"; asked only for a non-India nationality. */
  ociCardStatus?: string;
  /** "Yes" / "No"; asked only when the nationality IS India. */
  panCardStatus?: string;
  /** "Yes" / "No" — person of Indian origin; asked only for a non-India nationality. */
  indianOrigin?: string;
  agreeForOtp: boolean;
}

export interface Step2OtpVerificationDto {
  uniqueCode: string;
  emailOtp: string;
}

export interface Step4CompletionDto {
  uniqueCode: string;
  selfOrLegalEntity: string; // "Self" or "Legal Entity"
  nationality: string;
  countryOfResidence?: string;
  panCardStatus?: string;
  indianOrigin?: string;
  ociCardStatus?: string;
  entityName?: string;
  legalPanCard?: string;
  representativeCapacity?: string;
  securityRegulated?: string;
  termsAccepted: boolean;
  privacyPolicyAccepted: boolean;
  agreeForWhatsapp?: boolean;
  agreeForMarketing?: boolean;
  whatsappNumber?: string;
  whatsappCountryCode?: string;
}

export interface ApiResponseDto {
  success: boolean;
  message: string;
  uniqueCode?: string;
  nextStep?: string;
  data?: any;
  b2cAccountCreated?: boolean;
  investorId?: number;
  // Azure B2C object id from step-4 completion; the success screen links straight to
  // /investor/setpassword/{id} with it. Null when the B2C account could not be created.
  azureUserId?: string;
}

export const introducedInvestorService = {
  /**
   * Step 0: Initiate — plain Dataverse ss_name (e.g. INV-1744) or Laravel-encrypted token from introduce-investor1/...
   */
  initiateRegistration: (investorRef: string) =>
    apiClient.get<IntroducedInvestorDetailsDto>('/api/investor/introduced/initiate', {
      params: { investorRef }
    }),

  /**
   * Step 0 exit: the person chooses not to proceed. Soft abort — records the decision and
   * emails a confirmation; the link keeps working. 409 when the email is already registered.
   */
  discontinue: (investorRef: string, reason?: string) =>
    apiClient.post<ApiResponseDto>('/api/investor/introduced/discontinue', {
      investorRef,
      reason: reason?.trim() || undefined
    }),

  /**
   * Step 1: Record data consent
   */
  recordConsent: (dataverseInvestorId: string) =>
    apiClient.post<ApiResponseDto>('/api/investor/introduced/consent', null, {
      params: { dataverseInvestorId }
    }),

  /**
   * Step 2: Submit personal details and send OTP
   */
  submitStep1: (data: Step1RequestDto) =>
    apiClient.post<ApiResponseDto>('/api/investor/introduced/step1', data),

  /**
   * Step 3: Verify OTP
   */
  verifyOtp: (data: Step2OtpVerificationDto) =>
    apiClient.post<ApiResponseDto>('/api/investor/introduced/verify-otp', data),

  /**
   * Step 4: Complete registration
   */
  completeRegistration: (data: Step4CompletionDto) =>
    apiClient.post<ApiResponseDto>('/api/investor/introduced/complete', data),

  /**
   * Resend OTP
   */
  resendOtp: (uniqueCode: string) =>
    apiClient.post<ApiResponseDto>('/api/investor/introduced/resend-otp', null, {
      params: { uniqueCode }
    }),

  /**
   * Get session prefill data (email, name) for Step1 form
   */
  getSessionPrefill: (uniqueCode: string) =>
    apiClient.get<IntroducedInvestorDetailsDto>(`/api/investor/introduced/session/${uniqueCode}`)
};

export default introducedInvestorService;
